import * as React from "react";

type ToastVariant = "success" | "error" | "info";

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  onClose?: () => void;
  autoDismissMs?: number;
}

export function Toast({
  message,
  variant = "success",
  onClose,
  autoDismissMs = 4500,
}: ToastProps): React.JSX.Element {
  React.useEffect(() => {
    if (!onClose) return;
    const timer = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(timer);
  }, [onClose, autoDismissMs]);

  const colorMap: Record<ToastVariant, string> = {
    success:
      "bg-[var(--surface)] border-[var(--success,#16a34a)] text-[var(--fg)]",
    error: "bg-[var(--surface)] border-[var(--danger)] text-[var(--fg)]",
    info: "bg-[var(--surface)] border-[var(--accent)] text-[var(--fg)]",
  };

  const iconMap: Record<ToastVariant, React.JSX.Element> = {
    success: (
      <svg
        className="w-5 h-5 text-[var(--success,#16a34a)]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    error: (
      <svg
        className="w-5 h-5 text-[var(--danger)]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
    info: (
      <svg
        className="w-5 h-5 text-[var(--accent)]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  };

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`flex items-center gap-3 rounded-[var(--radius)] border px-4 py-3 shadow-lg min-w-[280px] max-w-[400px] ${colorMap[variant]}`}
    >
      <span className="flex-shrink-0">{iconMap[variant]}</span>
      <p className="flex-1 text-[14px] font-[500]">{message}</p>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть уведомление"
          className="flex-shrink-0 text-[var(--muted)] hover:text-[var(--fg)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}
